/**
 * src/components/layout/WriteErrorBanner.jsx
 *
 * App-shell banner shown when a dedicated write endpoint (risks-write,
 * documents-write, or the full-state persistence retry) fails after the
 * optimistic UI update has already been applied. Listens for the
 * 'seasuite:write-error' window event fired by useRiskState,
 * useDocumentState, and usePersistence. Dismissible; also auto-clears on
 * 'seasuite:write-recovered', which usePersistence fires when a retried
 * save finally lands.
 *
 * GH #6271: a caller with a specific, user-appropriate refusal message (e.g.
 * an EMP-B-002 evidence-immutability refusal) can attach it as
 * `detail.message` (writeErrors.js's dispatchWriteErrorBanner) — shown in
 * place of the generic text below. Every other caller keeps firing the bare
 * event, which still renders the generic text exactly as before.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  consumePendingWriteErrorNotice,
  clearPendingWriteErrorNotice,
} from '../../api/writeErrors.js';

export default function WriteErrorBanner() {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    // GH #6607: a notice raised while this component was not mounted reached
    // no listener at all. The boot-time teardown-backup replay in
    // usePersistence.js raises exactly this notice, on the paths where an edit
    // from the previous session could not be recovered — and it runs on the
    // line after `stateReady` flips, i.e. before App.jsx has committed the
    // render that mounts this banner. Drain the parked notice so the user is
    // told rather than the event being dispatched into an empty window.
    const parked = consumePendingWriteErrorNotice();
    if (parked) {
      setMessage(parked.message);
      setVisible(true);
    }
    const onError = (e) => {
      // A live listener has it — nothing left to drain on a later remount.
      clearPendingWriteErrorNotice();
      setMessage(e?.detail?.message || null);
      setVisible(true);
    };
    // A retry that finally lands clears the banner — once the edit is saved,
    // "changes may not have been saved" is no longer true, and leaving it up
    // sends the user chasing work that is already persisted.
    const onRecovered = () => setVisible(false);
    window.addEventListener('seasuite:write-error', onError);
    window.addEventListener('seasuite:write-recovered', onRecovered);
    return () => {
      window.removeEventListener('seasuite:write-error', onError);
      window.removeEventListener('seasuite:write-recovered', onRecovered);
    };
  }, []);

  const dismiss = useCallback(() => {
    clearPendingWriteErrorNotice();
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="alert"
      style={{
        background: 'var(--danger-bg, rgba(239,68,68,0.08))',
        borderBottom: '1px solid var(--danger)',
        color: 'var(--danger)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 20px',
        fontSize: '13px',
        fontFamily: 'var(--font-mono)',
        gap: 12,
        zIndex: 200,
        flexShrink: 0,
        pointerEvents: 'auto',
      }}
    >
      <span>
        <span style={{ fontWeight: 600 }}>Changes may not have been saved</span>
        {' — '}
        {message ||
          'a write to the server failed. Check your connection and re-apply the change, or reload the page.'}
      </span>
      <button
        onClick={dismiss}
        aria-label="Dismiss write error notice"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'inherit',
          fontSize: 18,
          lineHeight: 1,
          padding: '0 4px',
          opacity: 0.7,
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}
